import React, { createContext, useContext, useState, useEffect } from 'react';

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
}

interface CookieConsentContextType {
  cookiePreferences: CookiePreferences;
  showBanner: boolean;
  showSettings: boolean;
  handleAcceptAll: () => void;
  handleRejectNonEssential: () => void;
  handleSavePreferences: (preferences: CookiePreferences) => void;
  toggleSettings: () => void;
}

const CookieConsentContext = createContext<
  CookieConsentContextType | undefined
>(undefined);

const COOKIE_PREFERENCES_KEY = 'cookiePreferences';

export const CookieConsentProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [cookiePreferences, setCookiePreferences] = useState<CookiePreferences>(
    {
      essential: true,
      analytics: false,
      marketing: false,
    },
  );
  const [showBanner, setShowBanner] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [initialSettingsOpen, setInitialSettingsOpen] = useState(true);

  useEffect(() => {
    const savedPreferences = localStorage.getItem(COOKIE_PREFERENCES_KEY);
    if (savedPreferences) {
      setCookiePreferences(JSON.parse(savedPreferences));
      setShowBanner(false);
      setInitialSettingsOpen(false);
    }
  }, []);

  const savePreferences = (preferences: CookiePreferences) => {
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(preferences));
    setCookiePreferences(preferences);
    setShowBanner(false);
    setShowSettings(false);
    setInitialSettingsOpen(false);
  };

  const handleAcceptAll = () => {
    savePreferences({
      essential: true,
      analytics: true,
      marketing: true,
    });
  };

  const handleRejectNonEssential = () => {
    savePreferences({
      essential: true,
      analytics: false,
      marketing: false,
    });
  };

  const handleSavePreferences = (preferences: CookiePreferences) => {
    savePreferences({ ...preferences, essential: true });
  };

  const toggleSettings = () => {
    if (initialSettingsOpen) {
      // When opening settings for the first time, pre-select analytics and marketing
      setCookiePreferences({
        essential: true,
        analytics: true,
        marketing: true,
      });
    }
    setShowSettings(!showSettings);
  };

  return (
    <CookieConsentContext.Provider
      value={{
        cookiePreferences,
        showBanner,
        showSettings,
        handleAcceptAll,
        handleRejectNonEssential,
        handleSavePreferences,
        toggleSettings,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error(
      'useCookieConsent must be used within a CookieConsentProvider',
    );
  }
  return context;
};
